/**
 * EditReplenishmentModal (SR0.2) — chunked edit for the 補貨參數 replenishment
 * planning block (DD!53–67, J=SHOULD).
 *
 * Every field is optional/nullable and NEVER gates save or 產品就緒 (Fixed
 * decision 1) — the two-tier contract holds: readiness is computed on read and
 * this modal only tunes the reorder-suggestion inputs. The dormant `*Auto`
 * booleans + `preferredStockLevelDays` are preserved on save (spread from the
 * current product) but rendered NOWHERE until the v2 ladder rung.
 *
 * House pattern (EditCommercialModal): zodResolver, width 800, error messages
 * as i18n keys, useProductMutations store-first PATCH.
 */

import { useEffect, useMemo } from 'react';
import { Col, InputNumber, Modal, Row, Switch, Typography, theme } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens } from '@/styles/tokens';
import { useErrorHandler } from '@/lib/errors/useErrorHandler';
import { useProductMutations } from '@/hooks/useProducts';
import type { Product } from '@/types/api/product';

const { Text } = Typography;

const optionalNonNegative = z
  .number()
  .min(0, 'products:validation.nonNegative')
  .nullable()
  .optional();

const editReplenishmentSchema = z.object({
  reorderPoint: optionalNonNegative,
  preferredStockLevel: optionalNonNegative,
  reorderMultiple: optionalNonNegative,
  purchaseLeadTimeDays: optionalNonNegative,
  safetyStockDays: optionalNonNegative,
  // Demand can fall as well as rise — no lower bound on the % delta.
  expectedDemandChangePct: z.number().nullable().optional(),
  seasonalDemand: z.boolean(),
  doNotOrder: z.boolean(),
  preOrder: z.boolean(),
});

type EditReplenishmentFormData = z.infer<typeof editReplenishmentSchema>;

interface EditReplenishmentModalProps {
  open: boolean;
  product: Product;
  onCancel: () => void;
  onSaved: () => void;
}

export const EditReplenishmentModal = ({
  open,
  product,
  onCancel,
  onSaved,
}: EditReplenishmentModalProps) => {
  const { t } = useTranslation(['products', 'common']);
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const { handleError, handleSuccess } = useErrorHandler();
  const { updateProduct } = useProductMutations(product.id);

  const formValues = useMemo<EditReplenishmentFormData>(() => {
    const planning = product.planning ?? {};
    return {
      reorderPoint: planning.reorderPoint ?? null,
      preferredStockLevel: planning.preferredStockLevel ?? null,
      reorderMultiple: planning.reorderMultiple ?? null,
      purchaseLeadTimeDays: planning.purchaseLeadTimeDays ?? null,
      safetyStockDays: planning.safetyStockDays ?? null,
      expectedDemandChangePct: planning.expectedDemandChangePct ?? null,
      seasonalDemand: planning.seasonalDemand === true,
      doNotOrder: planning.doNotOrder === true,
      preOrder: planning.preOrder === true,
    };
  }, [product.planning]);

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm<EditReplenishmentFormData>({
    resolver: zodResolver(editReplenishmentSchema),
    defaultValues: formValues,
    values: formValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (open) reset(formValues);
  }, [open, formValues, reset]);

  const onSubmit = async (data: EditReplenishmentFormData) => {
    try {
      await updateProduct({
        planning: {
          // Preserve the dormant v2 fields (auto booleans, PSL days) untouched.
          ...(product.planning ?? {}),
          reorderPoint: data.reorderPoint ?? null,
          preferredStockLevel: data.preferredStockLevel ?? null,
          reorderMultiple: data.reorderMultiple ?? null,
          purchaseLeadTimeDays: data.purchaseLeadTimeDays ?? null,
          safetyStockDays: data.safetyStockDays ?? null,
          expectedDemandChangePct: data.expectedDemandChangePct ?? null,
          seasonalDemand: data.seasonalDemand,
          doNotOrder: data.doNotOrder,
          preOrder: data.preOrder,
        },
      });
      handleSuccess(t('products:messages.updated'));
      onSaved();
      onCancel();
    } catch (error) {
      handleError(error);
    }
  };

  const numberField = (
    name: keyof EditReplenishmentFormData,
    labelKey: string,
    options: { min?: number; precision?: number } = {}
  ) => (
    <Col span={8}>
      <Text style={formStyles.labelStyle}>{t(labelKey)}</Text>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <>
            <InputNumber
              value={(field.value as number | null) ?? null}
              onChange={(value) => {
                field.onChange(value ?? null);
                trigger(name);
              }}
              onBlur={field.onBlur}
              size="large"
              min={options.min}
              precision={options.precision}
              status={errors[name] ? 'error' : undefined}
              style={formStyles.inputStyle}
            />
            {errors[name] && (
              <Text style={formStyles.errorStyle}>{t(errors[name]?.message ?? '')}</Text>
            )}
          </>
        )}
      />
    </Col>
  );

  const switchField = (name: 'seasonalDemand' | 'doNotOrder' | 'preOrder', labelKey: string) => (
    <Col span={8}>
      <div className="tw-flex tw-items-center tw-gap-2" style={{ minHeight: '44px' }}>
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <Switch checked={field.value} onChange={(checked) => field.onChange(checked)} />
          )}
        />
        <Text style={{ ...formStyles.labelStyle, marginBottom: 0 }}>{t(labelKey)}</Text>
      </div>
    </Col>
  );

  return (
    <Modal
      title={t('products:planning.editTitle')}
      open={open}
      onCancel={onCancel}
      onOk={handleSubmit(onSubmit)}
      okText={t('common:actions.save')}
      cancelText={t('common:actions.cancel')}
      confirmLoading={isSubmitting}
      width={800}
      okButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      cancelButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
    >
      <form>
        <div style={formStyles.fieldContainerStyle}>
          <Text strong style={formStyles.sectionTitleStyle}>
            {t('products:planning.sectionTitle')}
          </Text>
          <Text
            type="secondary"
            style={{ fontSize: token.fontSizeSM, display: 'block', marginBottom: '16px' }}
          >
            {t('products:planning.editHint')}
          </Text>
          <Row gutter={[16, 16]}>
            {numberField('reorderPoint', 'products:fields.planning.reorderPoint', { min: 0 })}
            {numberField('preferredStockLevel', 'products:fields.planning.preferredStockLevel', {
              min: 0,
            })}
            {numberField('reorderMultiple', 'products:fields.planning.reorderMultiple', { min: 0 })}
            {numberField('purchaseLeadTimeDays', 'products:fields.planning.purchaseLeadTimeDays', {
              min: 0,
              precision: 0,
            })}
            {numberField('safetyStockDays', 'products:fields.planning.safetyStockDays', {
              min: 0,
              precision: 0,
            })}
            {numberField(
              'expectedDemandChangePct',
              'products:fields.planning.expectedDemandChangePct'
            )}
            {switchField('seasonalDemand', 'products:fields.planning.seasonalDemand')}
            {switchField('doNotOrder', 'products:fields.planning.doNotOrder')}
            {switchField('preOrder', 'products:fields.planning.preOrder')}
          </Row>
        </div>
      </form>
    </Modal>
  );
};

export default EditReplenishmentModal;
